import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { LogOut, School as SchoolIcon, Building2, ChevronDown, Eye } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { buildMenu, isReadOnlyRole, type MenuSection } from '@/config/roles'
import type { Role } from '@/types/auth'
import { cn } from '@/lib/cn'

// Folded sections, remembered per browser (a convenience only: a blocked storage just means
// every section starts open).
const FOLD_KEY = 'novitiq.admin.foldedSections'
function readFolded(): MenuSection[] {
  try {
    const raw = localStorage.getItem(FOLD_KEY)
    const v = raw ? JSON.parse(raw) : []
    return Array.isArray(v) ? v : []
  } catch {
    return []
  }
}
function writeFolded(v: MenuSection[]) {
  try {
    localStorage.setItem(FOLD_KEY, JSON.stringify(v))
  } catch {
    /* storage blocked: folding still works for this visit */
  }
}

interface Props {
  collapsed: boolean
  onNavigate?: () => void
  onLogout: () => void
}

function roleLabel(role: Role): string {
  switch (role) {
    case 'ADMIN':
      return 'Platform Admin'
    case 'SCHOOL_ADMIN':
      return 'School Admin'
    case 'PRINCIPAL':
      return 'Principal'
    case 'TEACHER':
      return 'Teacher'
    default:
      return role
  }
}

/** Determine which single menu route is active (longest matching prefix). */
function activeRoute(routes: string[], pathname: string): string | null {
  let best: string | null = null
  for (const r of routes) {
    if (pathname === r || pathname.startsWith(r + '/')) {
      if (!best || r.length > best.length) best = r
    }
  }
  return best
}

export function Sidebar({ collapsed, onNavigate, onLogout }: Props) {
  const user = useAuthStore((s) => s.user)
  const { pathname } = useLocation()
  const [folded, setFolded] = useState<MenuSection[]>(readFolded)
  if (!user) return null

  const sections = buildMenu({ role: user.role, is_default_school: user.is_default_school })
  const allRoutes = sections.flatMap((s) => s.items.flatMap((i) => i.routes))
  const active = activeRoute(allRoutes, pathname)
  const toggle = (sec: MenuSection) => {
    const next = folded.includes(sec) ? folded.filter((x) => x !== sec) : [...folded, sec]
    setFolded(next)
    writeFolded(next)
  }

  return (
    <div
      className={cn(
        'flex h-full flex-col bg-gradient-sidebar shadow-sidebar transition-[width] duration-180',
        collapsed ? 'w-20' : 'w-[260px] ultra:w-[300px]',
      )}
    >
      {/* Brand */}
      <div className={cn('flex flex-col gap-3.5 px-5 py-6', collapsed && 'items-center px-4')}>
        <div className="flex items-center gap-3.5">
          <div className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl bg-gradient-primary shadow-[0_4px_12px_rgba(99,102,241,0.4)]">
            <SchoolIcon size={22} className="text-white" />
          </div>
          {!collapsed && <span className="text-[20px] font-bold tracking-tight text-white">Novitiq</span>}
        </div>
        {!collapsed && user.school_name && (
          <div className="flex items-center gap-2 rounded-[10px] border border-white/10 bg-white/[0.04] px-3 py-2.5">
            <Building2 size={14} className="text-white/50" />
            <span className="truncate text-[11.5px] font-semibold tracking-wide text-white/75">{user.school_name}</span>
          </div>
        )}
      </div>

      <div className="mx-5 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      {/* Nav */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-3">
        {sections.map((section) => {
          const isFolded = !!section.label && folded.includes(section.section)
          const holdsActive = section.items.some((i) => active !== null && i.routes.includes(active))
          return (
            <div key={section.section} data-testid={`nav-section-${section.section}`}>
              {!collapsed && section.label && (
                <button
                  type="button"
                  onClick={() => toggle(section.section)}
                  aria-expanded={!isFolded}
                  className="flex w-full items-center justify-between px-3 pb-2 pt-5 text-[10px] font-bold uppercase tracking-[1.5px] text-white/30 hover:text-white/60"
                >
                  {section.label}
                  <ChevronDown size={12} className={cn('transition-transform', isFolded && '-rotate-90')} />
                </button>
              )}
              {collapsed && <div className="h-4" />}
              {(!isFolded || collapsed || holdsActive) &&
                section.items.map((item) => (
                  <NavItem
                    key={item.key}
                    icon={item.icon}
                    label={item.label}
                    to={item.route}
                    active={active !== null && item.routes.includes(active)}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                  />
                ))}
            </div>
          )
        })}
      </nav>

      {/* User footer */}
      <div className="m-3 flex items-center gap-3 rounded-md border border-white/[0.06] bg-white/[0.06] p-3.5">
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-gradient-primary text-[15px] font-bold text-white">
          {user.full_name.charAt(0).toUpperCase() || 'U'}
        </div>
        {!collapsed && (
          <>
            <div className="min-w-0 flex-1">
              <p className="truncate text-title-sm font-semibold text-white">{user.full_name}</p>
              <p className="text-label-sm font-medium text-primary-light/70">
                {roleLabel(user.role)}
                {isReadOnlyRole(user.role) && (
                  <span className="ml-1.5 inline-flex items-center gap-1 text-white/50">
                    <Eye size={11} /> view only
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onLogout}
              aria-label="Sign out"
              className="rounded-lg bg-white/[0.06] p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <LogOut size={18} />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function NavItem({
  icon: Icon,
  label,
  to,
  active,
  collapsed,
  onNavigate,
}: {
  icon: LucideIcon
  label: string
  to: string
  active: boolean
  collapsed: boolean
  onNavigate?: () => void
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={cn(
        'group my-0.5 flex items-center gap-3 rounded-md px-3.5 py-2.5 transition-colors duration-180',
        active ? 'border border-white/[0.08] bg-primary/[0.18]' : 'hover:bg-primary/10',
        collapsed && 'justify-center px-3',
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 flex-none items-center justify-center rounded-[9px]',
          active ? 'bg-gradient-primary shadow-[0_2px_8px_rgba(99,102,241,0.4)]' : 'bg-white/[0.06]',
        )}
      >
        <Icon size={18} className={cn(active ? 'text-white' : 'text-white/60 group-hover:text-white/90')} />
      </span>
      {!collapsed && (
        <span
          className={cn(
            'flex-1 text-[13.5px] tracking-tight',
            active ? 'font-semibold text-white' : 'font-medium text-white/70 group-hover:text-white/95',
          )}
        >
          {label}
        </span>
      )}
      {!collapsed && active && <span className="h-5 w-[3px] rounded-full bg-primary-light shadow-[0_0_6px_rgba(129,140,248,0.55)]" />}
    </Link>
  )
}
